/**
 * SlideoutWidthContext - Session-only persistence for slideout width.
 *
 * Stores the user's preferred slideout width during the session.
 * Resets to default on page refresh (no localStorage).
 */

import React, { createContext, useContext, useMemo, useState } from 'react';

import { DEFAULT_SLIDEOUT_WIDTH } from '@/lib/base/overlays/slideout-menu';

/* eslint-disable react-refresh/only-export-components -- standard context pattern: provider, hook, and context co-located */

/** Default slideout width in pixels — shared across all slideouts. */
const DEFAULT_WIDTH = DEFAULT_SLIDEOUT_WIDTH;

interface SlideoutWidthContextType {
  /** Current slideout width in pixels */
  width: number;
  /** Update the slideout width */
  setWidth: (width: number) => void;
  /**
   * Whether the user is currently dragging the slideout's resize handle.
   *
   * Consumers that size themselves against the slideout (the builder-mode chat
   * column) must suppress their width transition while this is true — during a
   * drag `width` changes on every mousemove, so a running transition restarts
   * ~60×/sec and the layout visibly lags the pointer. `ActionSidebarContext`
   * carries the same signal for the proposal sidebar.
   */
  isResizing: boolean;
  /** Report drag start/end — wired to `SlideoutMenu`'s `onResizingChange`. */
  setIsResizing: (isResizing: boolean) => void;
}

const SlideoutWidthContext = createContext<
  SlideoutWidthContextType | undefined
>(undefined);

/**
 * Hook to access slideout width context.
 *
 * @example
 * ```tsx
 * const { width, setWidth } = useSlideoutWidth();
 *
 * // Use width for SlideoutMenu initialWidth
 * <SlideoutMenu
 *   resizable
 *   initialWidth={width}
 *   onWidthChange={setWidth}
 * />
 * ```
 */
export function useSlideoutWidth(): SlideoutWidthContextType {
  const context = useContext(SlideoutWidthContext);
  if (context === undefined) {
    throw new Error(
      'useSlideoutWidth must be used within a SlideoutWidthProvider'
    );
  }
  return context;
}

interface SlideoutWidthProviderProps {
  children: React.ReactNode;
  /** Initial width (default: `DEFAULT_SLIDEOUT_WIDTH`) */
  initialWidth?: number;
}

/**
 * Provider for slideout width state.
 *
 * @example
 * ```tsx
 * <SlideoutWidthProvider>
 *   <App />
 * </SlideoutWidthProvider>
 * ```
 */
export function SlideoutWidthProvider({
  children,
  initialWidth = DEFAULT_WIDTH,
}: SlideoutWidthProviderProps): React.ReactElement {
  const [width, setWidth] = useState(initialWidth);
  const [isResizing, setIsResizing] = useState(false);

  // Memoize context value to prevent unnecessary re-renders of consumers.
  // `setWidth`/`setIsResizing` are useState setters, so they are already
  // stable — which also satisfies `SlideoutMenu`'s stable-reference
  // requirement for `onResizingChange`.
  const value = useMemo(
    () => ({ width, setWidth, isResizing, setIsResizing }),
    [width, isResizing]
  );

  return (
    <SlideoutWidthContext.Provider value={value}>
      {children}
    </SlideoutWidthContext.Provider>
  );
}
